import { useReactTable, createColumnHelper, flexRender, getCoreRowModel } from "@tanstack/react-table"
import { LEADERBOARD, LeaderboardProps, LEADERBOARDVIEW } from "@/types/types.leaderboards"
import { useAppSelector } from "@/app/redux/hooks/reduxHooks"
import { useEffect, useRef, useState } from "react"
import { addCommas } from "@/lib/addCommas"

type Props = {
    response: LeaderboardProps[]
    currentLeaderboard: string
    currentRankings: string
}

const TableComponent: React.FC<Props> = (props) => {
    const { response, currentLeaderboard, currentRankings } = props
    const columnHelper = createColumnHelper<LeaderboardProps>()
    const auth = useAppSelector((state) => state.auth)

    const displayMetric = () => {
        switch (currentLeaderboard) {
            case LEADERBOARDVIEW.MARKS:
                return "Marks"
            case LEADERBOARDVIEW.VALOR:
                return "Valor"
            case LEADERBOARDVIEW.MERIT:
                return "Merit"
        }
    }

    const columns = [
        columnHelper.accessor("Position", {
            header: () => "Rank",
            cell: (info) => info.renderValue()! + 1,
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor("StatValue", {
            header: () => displayMetric(),
            cell: (info) => addCommas(info.renderValue()!),
            footer: (info) => info.column.id,
        }),
        columnHelper.accessor("DisplayName", {
            header: () => "Username",
            cell: (info) => info.getValue(),
            footer: (info) => info.column.id,
        }),
    ]

    const [data, setData] = useState(() => [...response])
    const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() })

    useEffect(() => {
        setData([...response])
    }, [response])

    const currentRef = useRef<HTMLTableSectionElement>(null)

    useEffect(() => {
        if (currentRef.current && currentRankings === LEADERBOARD.FETCHAROUNDPLAYER) {
            currentRef.current.querySelectorAll("tr").forEach((tr) => {
                tr.querySelectorAll("td").forEach((td) => {
                    if (td.textContent?.includes(auth.displayName!)) {
                        td.scrollIntoView({ behavior: "smooth", block: "center" })
                    }
                })
            })
        }
    }, [currentRef, currentLeaderboard])

    return (
        <table className="w-full mt-5 overflow-x-scroll">
            <thead className="text-left text-primary-light">
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <th key={header.id} className="px-5 py-2 text-[21px]">
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody ref={currentRef}>
                {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="[&:nth-child(odd)]:bg-[#333] transition-all leaderboard-row">
                        {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-5 py-3 text-primary-light">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

export default TableComponent
